/** Turn a finished receipt or invoice into an income line for the shared project ledger. Pure, so it is easy to test. */
import type { Expense, TaxType } from "./types";

export interface DocumentSummary {
  kind: "receipt" | "invoice";
  number: string;
  /** YYYY-MM-DD */
  date: string;
  party: string;
  /** Grand total including tax. */
  total: number;
  currency: string;
  taxPct: number;
  taxLabel: string;
  dueDate?: string;
}

export function taxTypeFromLabel(label: string, pct: number): TaxType {
  if (!(pct > 0)) return "none";
  const l = label.toLowerCase();
  if (/\bvat\b/.test(l)) return "vat";
  if (/\bgst\b|\bhst\b|\bpst\b/.test(l)) return "gst";
  return "sales_tax";
}

/** The same document always gets the same id, so adding it twice updates the line instead of doubling it. */
export function ledgerId(kind: string, number: string): string {
  const clean = number.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 28) || "x";
  return `${kind === "invoice" ? "inv" : "rcp"}_${clean}`;
}

export function incomeFromDocument(d: DocumentSummary): Expense | null {
  if (!Number.isFinite(d.total) || d.total <= 0) return null;
  const word = d.kind === "invoice" ? "Invoice" : "Receipt";
  const party = d.party.trim();
  const rate = Number.isFinite(d.taxPct) && d.taxPct > 0 ? d.taxPct : undefined;
  return {
    id: ledgerId(d.kind, d.number),
    date: d.date,
    description: `${word} ${d.number}${party ? ` - ${party}` : ""}`.slice(0, 300),
    kind: "income",
    category: d.kind === "invoice" ? "Invoiced" : "Received",
    vendor: party || undefined,
    amount: Math.round(d.total * 100) / 100,
    currency: d.currency,
    taxType: taxTypeFromLabel(d.taxLabel, d.taxPct),
    taxRate: rate,
    taxIncluded: true,
    receipt: d.number,
    notes: d.dueDate ? `Due ${d.dueDate}` : undefined,
  };
}

export interface PurchaseOrderSummary {
  number: string;
  /** YYYY-MM-DD */
  date: string;
  vendor: string;
  total: number;
  currency: string;
  taxPct: number;
  taxLabel: string;
  category?: string;
  deliveryDate?: string;
}

/** A purchase order as a cost line. Same number gives the same id, so re-adding updates it. */
export function costFromPurchaseOrder(d: PurchaseOrderSummary): Expense | null {
  if (!Number.isFinite(d.total) || d.total <= 0) return null;
  const vendor = d.vendor.trim();
  const clean = d.number.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 28) || "x";
  return {
    id: `po_${clean}`,
    date: d.date,
    description: `Purchase order ${d.number}${vendor ? ` - ${vendor}` : ""}`.slice(0, 300),
    kind: "cost",
    category: d.category?.trim() || undefined,
    vendor: vendor || undefined,
    amount: Math.round(d.total * 100) / 100,
    currency: d.currency,
    taxType: taxTypeFromLabel(d.taxLabel, d.taxPct),
    taxRate: d.taxPct > 0 ? d.taxPct : undefined,
    taxIncluded: true,
    receipt: d.number,
    notes: d.deliveryDate ? `Delivery ${d.deliveryDate}` : undefined,
  };
}

/** What an accepted quote becomes on a purchase order: the vendor and one line. */
export function lineFromQuote(q: { vendor: string; item: string; amount: number; currency: string }): { vendor: string; description: string; unitPrice: string; currency: string } {
  return { vendor: q.vendor, description: q.item, unitPrice: String(Math.round(q.amount * 100) / 100), currency: q.currency };
}
